import { ColDef, ColGroupDef, ValueFormatterParams, ValueGetterParams } from 'ag-grid-community';
import { get as lodashGet } from 'lodash-es';
import { StringListCellRenderer } from '../../../../../../components/AgTable/cell-renderers/StringListCellRenderer';
import { ICustomNumberFormatConfig, StandardFormatterId } from '@foresightdata/web-common-core';
import { FMT } from '@foresightdata/web-common-formatter-service';
import { ColumnMeta } from '../../../../../../types';
import { getIsoDateColDef } from '../../../../../../util/ag-grid-utils';
import {
  getCompactExcelCellClassRules,
  getExcelFormatterId,
} from '../../../../../../util/excel-formatter-service';
import { roundToPrecision } from '../../../../../../util/math-utils';
import { TooltipWrapperComponent } from '../../../../../../util/TooltipWrapperComponent';
import { ImageCellRenderer } from './TypeCellRenderers/ImageCellRenderer';
import { LinkRenderer } from './TypeCellRenderers/LinkRenderer';

export function getTypeBasedConfigs(
  column: ColumnMeta,
  customValueGetter?: (params: ValueGetterParams) => unknown
): Partial<ColDef | ColGroupDef> | null {
  if (column.type === 'number') {
    const excelFormatterId = getExcelFormatterId(column.formatting as StandardFormatterId);
    // Abbreviated formatters pick their Excel style per cell instead, so the base id is left off to
    // keep exactly one style matching each cell.
    const compactExcelRules = getCompactExcelCellClassRules(column.formatting as string);
    const commonNumberCellClasses =
      excelFormatterId && !compactExcelRules
        ? ['monospace', 'text-align-right', excelFormatterId]
        : ['monospace', 'text-align-right'];

    const numberColumnSettings: Partial<ColDef> = {
      headerClass: 'ag-left-aligned-header',
      type: 'rightAligned',
      filter: 'agNumberColumnFilter',
      sortingOrder: ['desc', 'asc', null],
      cellClass: [...commonNumberCellClasses],
      ...(compactExcelRules ? { cellClassRules: compactExcelRules } : {}),
      enableValue: true,
    };

    const formattingModel = FMT.get().getFormatterModel(column.formatting as StandardFormatterId);
    const maxDp = (formattingModel?.config as ICustomNumberFormatConfig)?.maximumFractionDigits;
    if (typeof maxDp === 'number') {
      numberColumnSettings.filterValueGetter = (params: ValueGetterParams) => {
        const value = customValueGetter ? customValueGetter(params) : lodashGet(params?.data, column.path);
        return typeof value === 'number' ? roundToPrecision(value, maxDp) : value;
      };
    }

    if (column.path === 'irr') {
      return {
        ...numberColumnSettings,
        type: ['rightAligned', 'percent'],
      };
    }

    if (column.formatting === 'percent') {
      return {
        ...numberColumnSettings,
        type: ['rightAligned', 'percent'],
      };
    }

    return numberColumnSettings;
  }

  if (column.type === 'stringArray') {
    return {
      cellRenderer: StringListCellRenderer,
      cellRendererParams: {
        title: column.displayName,
      },
    };
  }

  if (column.formatting === 'longText') {
    return {
      cellRenderer: TooltipWrapperComponent,
    };
  }

  if (column.type === 'link' && column.formatting === 'url') {
    return {
      cellRenderer: LinkRenderer,
      valueFormatter: (params: ValueFormatterParams) => {
        const value = lodashGet(params.data, column.path);
        if (!value) {
          return '';
        }
        return value;
      },
    };
  }

  if (column.type === 'link' && column.formatting === 'image') {
    return {
      cellRenderer: ImageCellRenderer,
    };
  }

  if (column.type === 'date' || column.formatting === 'date') {
    return getIsoDateColDef({
      headerClass: 'ag-left-aligned-header',
      type: ['rightAligned'],
    });
  }

  return null;
}
